'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import ProductSpecsHoverCard from '@/components/ProductSpecsHoverCard';
import { buildProductCardSpecBadges } from '@/lib/product-spec-badges';
import { productImageOrFallback } from '@/lib/products/product-image';

function productDiscountPercent(product: ProductItem) {
  const price = Number(product.price || 0);
  const original = Number(product.original_price || 0);
  if (original > price && original > 0)
    return Math.min(99, Math.round((1 - price / original) * 100));
  return 0;
}

function ProductSpecIcon({ index }: { index: number }) {
  const paths = [
    'M8 1.5 2.5 4.5v7L8 14.5l5.5-3v-7L8 1.5Zm0 2.2 3.5 1.9L8 7.5 4.5 5.6 8 3.7Zm-3.8 3 3 1.6v3.4l-3-1.6V6.7Zm7.6 0v3.4l-3 1.6V8.3l3-1.6Z',
    'M3 2h10a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H9.5L8 14l-1.5-2H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Zm1 3v1h8V5H4Zm0 3v1h5V8H4Z',
    'M2 3h12v8H9.5v2H12v1H4v-1h2.5v-2H2V3Zm1.5 1.5v5h9v-5h-9Z',
    'M4 2h8v12H4V2Zm1.5 1.5v9h5v-9h-5ZM7 11h2v1H7v-1Z',
    'M2 4.5A2.5 2.5 0 0 1 4.5 2h7A2.5 2.5 0 0 1 14 4.5v7a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 2 11.5v-7Zm2.5-1A1 1 0 0 0 3.5 4.5v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-7ZM5 5h6v1H5V5Zm0 2.5h6v1H5v-1Zm0 2.5h4v1H5v-1Z',
    'M8 1.5A6.5 6.5 0 1 1 8 14.5 6.5 6.5 0 0 1 8 1.5Zm0 2A4.5 4.5 0 1 0 8 12.5 4.5 4.5 0 0 0 8 3.5Zm-.5 1.5h1v3.2l2.2 1.3-.5.9-2.7-1.6V5Z',
  ];

  return (
    <svg
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5 shrink-0 text-slate-500"
      fill="currentColor"
      aria-hidden
    >
      <path d={paths[index % paths.length]} />
    </svg>
  );
}

function ProductCartIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3.5 4.5h2.2l2.1 10.2a2 2 0 0 0 2 1.6h7.5a2 2 0 0 0 1.9-1.4l1.3-5.2H7" />
      <path d="M8.4 9.7h11.7" />
      <path d="M9.3 13h9.9" />
      <path d="M11.5 9.7v6.6" />
      <path d="M16.1 9.7v6.6" />
      <circle cx="10.2" cy="20" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="18" cy="20" r="1.35" fill="currentColor" stroke="none" />
    </svg>
  );
}

const CategoryProductCard = React.memo(({ product }: { product: ProductItem }) => {
  const specsList = buildProductCardSpecBadges(product.specs, product.full_specs, product.name);
  const pct = productDiscountPercent(product);

  return (
    <Link
      href={`/san-pham/${product.slug}`}
      className="group relative z-0 flex min-h-[552px] flex-col bg-white border border-slate-200 rounded-none p-3 hover:z-[10000] hover:border-slate-300"
    >
      <span
        className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center bg-transparent text-black"
        aria-hidden
      >
        <ProductCartIcon />
      </span>
      <div className="group/spec-trigger relative mb-3 aspect-[1.18/1] overflow-visible bg-white pt-5">
        <AppImage
          src={productImageOrFallback(product.image)}
          alt={product.name}
          fill
          sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 20vw"
          className="object-contain p-3 mix-blend-multiply"
        />
        <ProductSpecsHoverCard
          name={product.name}
          price={Number(product.price || 0)}
          originalPrice={Number(product.original_price || 0)}
          stock={Number(product.stock || 0)}
          fullSpecs={product.full_specs}
          specsText={product.specs}
        />
      </div>
      <h3 className="mb-2 min-h-[7.1rem] break-words text-left text-[14px] font-bold leading-[1.45] text-slate-800">
        {product.name}
      </h3>
      <div className="mb-3 bg-[#ececec] px-2.5 py-2 text-[12px] text-slate-600">
        {specsList.length ? (
          <div className="grid grid-cols-2 gap-x-2 gap-y-1">
            {specsList.map((spec, specIndex) => (
              <div key={`${spec}-${specIndex}`} className="flex min-w-0 items-center gap-1.5">
                <ProductSpecIcon index={specIndex} />
                <span className="truncate leading-snug">{spec}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="line-clamp-1">Thông số đang cập nhật</div>
        )}
      </div>
      <div className="mt-auto text-left">
        {Number(product.original_price || 0) > Number(product.price || 0) ? (
          <div className="mb-0.5 text-[13px] text-slate-500 line-through">
            {`${new Intl.NumberFormat('vi-VN').format(Number(product.original_price || 0))}₫`}
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[18px] font-black leading-none text-red-600">
            {`${new Intl.NumberFormat('vi-VN').format(Number(product.price || 0))}₫`}
          </span>
          {pct > 0 ? (
            <span className="border border-red-500 px-1.5 py-0.5 text-[12px] font-medium leading-none text-red-600">
              -{pct}%
            </span>
          ) : null}
        </div>
        <div className="mt-2 text-[13px] font-semibold text-orange-500">
          0.0 ★ <span className="font-normal text-slate-500">(0 đánh giá)</span>
        </div>
      </div>
    </Link>
  );
});

CategoryProductCard.displayName = 'CategoryProductCard';
import { laptopFilterGroups } from '@/lib/laptop-filter-config';
import { vgaFilterGroups } from '@/lib/vga-filter-config';
import { ramFilterGroups } from '@/lib/ram-filter-config';
import { ssdFilterGroups } from '@/lib/ssd-filter-config';
import { hddFilterGroups } from '@/lib/hdd-filter-config';
import { cpuFilterGroups } from '@/lib/cpu-filter-config';
import { mainboardFilterGroups } from '@/lib/mainboard-filter-config';
import { caseFilterGroups } from '@/lib/case-filter-config';
import { keyboardFilterGroups } from '@/lib/keyboard-filter-config';
import { mouseFilterGroups } from '@/lib/mouse-filter-config';
import { headsetFilterGroups } from '@/lib/headset-filter-config';
import { networkingFilterGroups } from '@/lib/networking-filter-config';
import { printerFilterGroups } from '@/lib/printer-filter-config';
import { upsFilterGroups } from '@/lib/ups-filter-config';
import { cameraFilterGroups } from '@/lib/camera-filter-config';
import { monitorFilterGroups } from '@/lib/monitor-filter-config';
import { coolingFilterGroups } from '@/lib/cooling-filter-config';
import { psuFilterGroups } from '@/lib/psu-filter-config';
import { webcamFilterGroups } from '@/lib/webcam-filter-config';
import {
  containsCatalogKeyword,
  isLikelyProductType,
  normalizeCatalogText,
} from '@/lib/catalog/guards';

type ProductItem = {
  id: string;
  name: string;
  slug: string;
  image: string | null;
  price: number;
  original_price: number;
  sku: string | null;
  specs: string | null;
  stock: number;
  full_specs: Record<string, string>;
  tags?: string[];
};

type SortMode = 'latest' | 'price-asc' | 'price-desc' | 'name-asc';

type LaptopFilterMap = Record<string, Set<string>>;

const LAPTOP_FILTER_PREFIX = 'lf_';
const VGA_FILTER_PREFIX = 'vf_';
const RAM_FILTER_PREFIX = 'rf_';
const SSD_FILTER_PREFIX = 'sf_';
const HDD_FILTER_PREFIX = 'hf_';
const CPU_FILTER_PREFIX = 'cf_';
const MAINBOARD_FILTER_PREFIX = 'mf_';
const CASE_FILTER_PREFIX = 'caf_';
const KEYBOARD_FILTER_PREFIX = 'kf_';
const MOUSE_FILTER_PREFIX = 'msf_';
const HEADSET_FILTER_PREFIX = 'hsf_';
const NETWORKING_FILTER_PREFIX = 'nwf_';
const PRINTER_FILTER_PREFIX = 'ptf_';
const UPS_FILTER_PREFIX = 'upf_';
const CAMERA_FILTER_PREFIX = 'camf_';
const MONITOR_FILTER_PREFIX = 'mnf_';
const COOLING_FILTER_PREFIX = 'clf_';
const PSU_FILTER_PREFIX = 'psf_';
const WEBCAM_FILTER_PREFIX = 'wcf_';
const PRODUCTS_PER_PAGE = 25;

const laptopFilterKeywords: Record<string, Record<string, string[]>> = laptopFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const vgaFilterKeywords: Record<string, Record<string, string[]>> = vgaFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const ramFilterKeywords: Record<string, Record<string, string[]>> = ramFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const ssdFilterKeywords: Record<string, Record<string, string[]>> = ssdFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const hddFilterKeywords: Record<string, Record<string, string[]>> = hddFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const cpuFilterKeywords: Record<string, Record<string, string[]>> = cpuFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const mainboardFilterKeywords: Record<
  string,
  Record<string, string[]>
> = mainboardFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const caseFilterKeywords: Record<string, Record<string, string[]>> = caseFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const keyboardFilterKeywords: Record<
  string,
  Record<string, string[]>
> = keyboardFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const mouseFilterKeywords: Record<string, Record<string, string[]>> = mouseFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const headsetFilterKeywords: Record<string, Record<string, string[]>> = headsetFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const networkingFilterKeywords: Record<
  string,
  Record<string, string[]>
> = networkingFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const printerFilterKeywords: Record<string, Record<string, string[]>> = printerFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const upsFilterKeywords: Record<string, Record<string, string[]>> = upsFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const cameraFilterKeywords: Record<string, Record<string, string[]>> = cameraFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const monitorFilterKeywords: Record<string, Record<string, string[]>> = monitorFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const coolingFilterKeywords: Record<string, Record<string, string[]>> = coolingFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const psuFilterKeywords: Record<string, Record<string, string[]>> = psuFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

const webcamFilterKeywords: Record<string, Record<string, string[]>> = webcamFilterGroups.reduce(
  (acc, group) => {
    acc[group.id] = group.options.reduce(
      (inner, option) => {
        inner[option.value] =
          option.keywords && option.keywords.length ? option.keywords : [option.label];
        return inner;
      },
      {} as Record<string, string[]>
    );
    return acc;
  },
  {} as Record<string, Record<string, string[]>>
);

function formatPrice(value: number) {
  return `${new Intl.NumberFormat('vi-VN').format(Number(value || 0))}₫`;
}

export default function CategoryProductListing({
  products,
  categorySlug,
  selectedSubSlug,
}: {
  products: ProductItem[];
  categorySlug?: string;
  selectedSubSlug?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [sortMode, setSortMode] = useState<SortMode>('latest');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const searchParams = useSearchParams();
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const currentPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const minPrice = parseInt(searchParams.get('min_price') || '0', 10);
  const maxPrice = parseInt(searchParams.get('max_price') || '100000000', 10);
  const currentSubSlug = String(selectedSubSlug || '').toLowerCase();
  const isCpuSub = currentSubSlug.includes('cpu');
  const isMainboardSub =
    currentSubSlug.includes('mainboard') ||
    currentSubSlug.includes('bo-mach-chu') ||
    currentSubSlug.includes('motherboard');
  const isVgaSub = currentSubSlug.includes('vga');
  const isRamSub = currentSubSlug.includes('ram');
  const isSsdSub = currentSubSlug.includes('ssd');
  const isHddSub = currentSubSlug.includes('hdd');
  const isCaseSub =
    currentSubSlug.includes('case') ||
    currentSubSlug.includes('vo-may') ||
    currentSubSlug.includes('thung-may');
  const isKeyboardSub = currentSubSlug.includes('ban-phim') || currentSubSlug.includes('keyboard');
  const isMouseSub = currentSubSlug.includes('chuot') || currentSubSlug.includes('mouse');
  const isHeadsetSub =
    currentSubSlug.includes('tai-nghe') ||
    currentSubSlug.includes('headset') ||
    currentSubSlug.includes('headphone');
  const isNetworkingSub =
    currentSubSlug.includes('mang') ||
    currentSubSlug.includes('wifi') ||
    currentSubSlug.includes('router') ||
    currentSubSlug.includes('switch');
  const isPrinterSub =
    currentSubSlug.includes('may-in') ||
    currentSubSlug.includes('printer') ||
    currentSubSlug.includes('scan');
  const isUpsSub = currentSubSlug.includes('ups');
  const isCameraSub = currentSubSlug.includes('camera') || currentSubSlug.includes('cam ');
  const isMonitorSub = currentSubSlug.includes('monitor') || currentSubSlug.includes('man-hinh');
  const isCoolingSub = currentSubSlug.includes('cooling') || currentSubSlug.includes('tan-nhiet');
  const isPsuSub = currentSubSlug.includes('psu') || currentSubSlug.includes('nguon');
  const isWebcamSub = currentSubSlug.includes('webcam');

  const laptopFilters = useMemo(() => {
    if (categorySlug !== 'laptop') return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(laptopFilterKeywords)) {
      const raw = searchParams.get(`${LAPTOP_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, searchParams]);

  const vgaFilters = useMemo(() => {
    const isVga = categorySlug === 'vga' || (isVgaSub && !isCpuSub && !isMainboardSub);
    if (!isVga) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(vgaFilterKeywords)) {
      const raw = searchParams.get(`${VGA_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isCpuSub, isMainboardSub, isVgaSub, searchParams]);

  const ramFilters = useMemo(() => {
    const isRam = categorySlug === 'ram' || (isRamSub && !isSsdSub && !isHddSub);
    if (!isRam) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(ramFilterKeywords)) {
      const raw = searchParams.get(`${RAM_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isHddSub, isRamSub, isSsdSub, searchParams]);

  const ssdFilters = useMemo(() => {
    const isSsd = categorySlug === 'ssd' || isSsdSub;
    if (!isSsd) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(ssdFilterKeywords)) {
      const raw = searchParams.get(`${SSD_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isSsdSub, searchParams]);

  const hddFilters = useMemo(() => {
    const isHdd = categorySlug === 'hdd' || isHddSub;
    if (!isHdd) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(hddFilterKeywords)) {
      const raw = searchParams.get(`${HDD_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isHddSub, searchParams]);

  const cpuFilters = useMemo(() => {
    const isCpu = categorySlug === 'cpu' || (isCpuSub && !isMainboardSub && !isVgaSub);
    if (!isCpu) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(cpuFilterKeywords)) {
      const raw = searchParams.get(`${CPU_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isCpuSub, isMainboardSub, isVgaSub, searchParams]);

  const mainboardFilters = useMemo(() => {
    const isMainboard = categorySlug === 'mainboard' || isMainboardSub;
    if (!isMainboard) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(mainboardFilterKeywords)) {
      const raw = searchParams.get(`${MAINBOARD_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isMainboardSub, searchParams]);

  const caseFilters = useMemo(() => {
    const isCase =
      categorySlug === 'case' ||
      categorySlug === 'case-tan-nguon' ||
      (isCaseSub && !isCpuSub && !isMainboardSub && !isVgaSub);
    if (!isCase) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(caseFilterKeywords)) {
      const raw = searchParams.get(`${CASE_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isCaseSub, isCpuSub, isMainboardSub, isVgaSub, searchParams]);

  const keyboardFilters = useMemo(() => {
    const isKeyboard = categorySlug === 'ban-phim' || categorySlug === 'keyboard' || isKeyboardSub;
    if (!isKeyboard) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(keyboardFilterKeywords)) {
      const raw = searchParams.get(`${KEYBOARD_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isKeyboardSub, searchParams]);

  const mouseFilters = useMemo(() => {
    const isMouse = categorySlug === 'chuot' || categorySlug === 'mouse' || isMouseSub;
    if (!isMouse) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(mouseFilterKeywords)) {
      const raw = searchParams.get(`${MOUSE_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isMouseSub, searchParams]);

  const headsetFilters = useMemo(() => {
    const isHeadset = categorySlug === 'tai-nghe' || categorySlug === 'headset' || isHeadsetSub;
    if (!isHeadset) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(headsetFilterKeywords)) {
      const raw = searchParams.get(`${HEADSET_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isHeadsetSub, searchParams]);

  const networkingFilters = useMemo(() => {
    const isNetworking =
      categorySlug === 'thiet-bi-mang' || categorySlug === 'networking' || isNetworkingSub;
    if (!isNetworking) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(networkingFilterKeywords)) {
      const raw = searchParams.get(`${NETWORKING_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isNetworkingSub, searchParams]);

  const printerFilters = useMemo(() => {
    const isPrinter =
      categorySlug === 'may-in' ||
      categorySlug === 'printer' ||
      categorySlug === 'may-in-mau' ||
      categorySlug === 'may-in-den-trang' ||
      isPrinterSub;
    if (!isPrinter) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(printerFilterKeywords)) {
      const raw = searchParams.get(`${PRINTER_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isPrinterSub, searchParams]);

  const upsFilters = useMemo(() => {
    const isUps = categorySlug === 'ups' || isUpsSub;
    if (!isUps) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(upsFilterKeywords)) {
      const raw = searchParams.get(`${UPS_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isUpsSub, searchParams]);

  const cameraFilters = useMemo(() => {
    const isCamera =
      categorySlug === 'camera' ||
      categorySlug === 'camera-webcam' ||
      categorySlug === 'camera-trong-nha' ||
      categorySlug === 'camera-ngoai-troi' ||
      isCameraSub;
    if (!isCamera) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(cameraFilterKeywords)) {
      const raw = searchParams.get(`${CAMERA_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isCameraSub, searchParams]);

  const monitorFilters = useMemo(() => {
    const isMonitor = categorySlug === 'man-hinh' || categorySlug === 'monitor' || isMonitorSub;
    if (!isMonitor) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(monitorFilterKeywords)) {
      const raw = searchParams.get(`${MONITOR_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isMonitorSub, searchParams]);

  const coolingFilters = useMemo(() => {
    const isCooling = categorySlug === 'cooling' || isCoolingSub;
    if (!isCooling) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(coolingFilterKeywords)) {
      const raw = searchParams.get(`${COOLING_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isCoolingSub, searchParams]);

  const psuFilters = useMemo(() => {
    const isPsu = categorySlug === 'psu' || isPsuSub;
    if (!isPsu) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(psuFilterKeywords)) {
      const raw = searchParams.get(`${PSU_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isPsuSub, searchParams]);

  const webcamFilters = useMemo(() => {
    const isWebcam = categorySlug === 'webcam' || isWebcamSub;
    if (!isWebcam) return null;
    const map: LaptopFilterMap = {};
    for (const key of Object.keys(webcamFilterKeywords)) {
      const raw = searchParams.get(`${WEBCAM_FILTER_PREFIX}${key}`) || '';
      map[key] = new Set(
        raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
    }
    return map;
  }, [categorySlug, isWebcamSub, searchParams]);

  const filteredProducts = useMemo(() => {
    const activeLaptopGroups = laptopFilters
      ? Object.entries(laptopFilters).filter(([, set]) => set.size)
      : [];
    const activeVgaGroups = vgaFilters
      ? Object.entries(vgaFilters).filter(([, set]) => set.size)
      : [];
    const activeRamGroups = ramFilters
      ? Object.entries(ramFilters).filter(([, set]) => set.size)
      : [];
    const activeSsdGroups = ssdFilters
      ? Object.entries(ssdFilters).filter(([, set]) => set.size)
      : [];
    const activeHddGroups = hddFilters
      ? Object.entries(hddFilters).filter(([, set]) => set.size)
      : [];
    const activeCpuGroups = cpuFilters
      ? Object.entries(cpuFilters).filter(([, set]) => set.size)
      : [];
    const activeMainboardGroups = mainboardFilters
      ? Object.entries(mainboardFilters).filter(([, set]) => set.size)
      : [];
    const activeCaseGroups = caseFilters
      ? Object.entries(caseFilters).filter(([, set]) => set.size)
      : [];
    const activeKeyboardGroups = keyboardFilters
      ? Object.entries(keyboardFilters).filter(([, set]) => set.size)
      : [];
    const activeMouseGroups = mouseFilters
      ? Object.entries(mouseFilters).filter(([, set]) => set.size)
      : [];
    const activeHeadsetGroups = headsetFilters
      ? Object.entries(headsetFilters).filter(([, set]) => set.size)
      : [];
    const activeNetworkingGroups = networkingFilters
      ? Object.entries(networkingFilters).filter(([, set]) => set.size)
      : [];
    const activePrinterGroups = printerFilters
      ? Object.entries(printerFilters).filter(([, set]) => set.size)
      : [];
    const activeUpsGroups = upsFilters
      ? Object.entries(upsFilters).filter(([, set]) => set.size)
      : [];
    const activeCameraGroups = cameraFilters
      ? Object.entries(cameraFilters).filter(([, set]) => set.size)
      : [];
    const activeMonitorGroups = monitorFilters
      ? Object.entries(monitorFilters).filter(([, set]) => set.size)
      : [];
    const activeCoolingGroups = coolingFilters
      ? Object.entries(coolingFilters).filter(([, set]) => set.size)
      : [];
    const activePsuGroups = psuFilters
      ? Object.entries(psuFilters).filter(([, set]) => set.size)
      : [];
    const activeWebcamGroups = webcamFilters
      ? Object.entries(webcamFilters).filter(([, set]) => set.size)
      : [];

    const hasAttributeFilters =
      !activeLaptopGroups.length &&
      !activeVgaGroups.length &&
      !activeRamGroups.length &&
      !activeSsdGroups.length &&
      !activeHddGroups.length &&
      !activeCpuGroups.length &&
      !activeMainboardGroups.length &&
      !activeCaseGroups.length &&
      !activeKeyboardGroups.length &&
      !activeMouseGroups.length &&
      !activeHeadsetGroups.length &&
      !activeNetworkingGroups.length &&
      !activePrinterGroups.length &&
      !activeUpsGroups.length &&
      !activeCameraGroups.length &&
      !activeMonitorGroups.length &&
      !activeCoolingGroups.length &&
      !activePsuGroups.length &&
      !activeWebcamGroups.length;
    const hasPriceFilter = minPrice > 0 || maxPrice < 100000000;

    if (hasAttributeFilters && !hasPriceFilter) return products;

    return products.filter((product) => {
      const price = Number(product.price || 0);
      if (minPrice > 0 && price < minPrice) return false;
      if (maxPrice < 100000000 && price > maxPrice) return false;

      const tagsText = normalizeCatalogText((product.tags || []).join(' '));
      const skuText = normalizeCatalogText(product.sku || '');
      const haystack = normalizeCatalogText(
        [
          product.name,
          product.specs || '',
          JSON.stringify(product.full_specs || {}),
          product.sku || '',
          tagsText,
        ].join(' ')
      );

      // Loại bỏ các gate theo loại sản phẩm ở đây vì Database đã filter đúng theo category_id/tags.
      // Việc re-filter ở frontend bằng keyword quá khắt khe làm mất sản phẩm (ví dụ: Chuột không có chữ "chuột" trong tên).

      const matchLaptop =
        !activeLaptopGroups.length ||
        activeLaptopGroups.every(([groupId, values]) => {
          const keywordsMap = laptopFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchVga =
        !activeVgaGroups.length ||
        activeVgaGroups.every(([groupId, values]) => {
          const keywordsMap = vgaFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchRam =
        !activeRamGroups.length ||
        activeRamGroups.every(([groupId, values]) => {
          const keywordsMap = ramFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchSsd =
        !activeSsdGroups.length ||
        activeSsdGroups.every(([groupId, values]) => {
          const keywordsMap = ssdFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchHdd =
        !activeHddGroups.length ||
        activeHddGroups.every(([groupId, values]) => {
          const keywordsMap = hddFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchCpu =
        !activeCpuGroups.length ||
        activeCpuGroups.every(([groupId, values]) => {
          const keywordsMap = cpuFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchMainboard =
        !activeMainboardGroups.length ||
        activeMainboardGroups.every(([groupId, values]) => {
          const keywordsMap = mainboardFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchCase =
        !activeCaseGroups.length ||
        activeCaseGroups.every(([groupId, values]) => {
          const keywordsMap = caseFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchKeyboard =
        !activeKeyboardGroups.length ||
        activeKeyboardGroups.every(([groupId, values]) => {
          const keywordsMap = keyboardFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchMouse =
        !activeMouseGroups.length ||
        activeMouseGroups.every(([groupId, values]) => {
          const keywordsMap = mouseFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchHeadset =
        !activeHeadsetGroups.length ||
        activeHeadsetGroups.every(([groupId, values]) => {
          const keywordsMap = headsetFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchNetworking =
        !activeNetworkingGroups.length ||
        activeNetworkingGroups.every(([groupId, values]) => {
          const keywordsMap = networkingFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchPrinter =
        !activePrinterGroups.length ||
        activePrinterGroups.every(([groupId, values]) => {
          const keywordsMap = printerFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchUps =
        !activeUpsGroups.length ||
        activeUpsGroups.every(([groupId, values]) => {
          const keywordsMap = upsFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchCamera =
        !activeCameraGroups.length ||
        activeCameraGroups.every(([groupId, values]) => {
          const keywordsMap = cameraFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchMonitor =
        !activeMonitorGroups.length ||
        activeMonitorGroups.every(([groupId, values]) => {
          const keywordsMap = monitorFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchCooling =
        !activeCoolingGroups.length ||
        activeCoolingGroups.every(([groupId, values]) => {
          const keywordsMap = coolingFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchPsu =
        !activePsuGroups.length ||
        activePsuGroups.every(([groupId, values]) => {
          const keywordsMap = psuFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      const matchWebcam =
        !activeWebcamGroups.length ||
        activeWebcamGroups.every(([groupId, values]) => {
          const keywordsMap = webcamFilterKeywords[groupId] || {};
          return Array.from(values).some((value) => {
            const keywords = keywordsMap[value] || [];
            if (!keywords.length) return false;
            return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
          });
        });

      return (
        matchLaptop &&
        matchVga &&
        matchRam &&
        matchSsd &&
        matchHdd &&
        matchCpu &&
        matchMainboard &&
        matchCase &&
        matchKeyboard &&
        matchMouse &&
        matchHeadset &&
        matchNetworking &&
        matchPrinter &&
        matchUps &&
        matchCamera &&
        matchMonitor &&
        matchCooling &&
        matchPsu &&
        matchWebcam
      );
    });
  }, [
    laptopFilters,
    products,
    minPrice,
    maxPrice,
    vgaFilters,
    ramFilters,
    ssdFilters,
    hddFilters,
    cpuFilters,
    mainboardFilters,
    caseFilters,
    keyboardFilters,
    mouseFilters,
    headsetFilters,
    networkingFilters,
    printerFilters,
    upsFilters,
    cameraFilters,
    monitorFilters,
    coolingFilters,
    psuFilters,
    webcamFilters,
    isCaseSub,
    isKeyboardSub,
    isMouseSub,
    isHeadsetSub,
    isNetworkingSub,
    isPrinterSub,
    isUpsSub,
    isCameraSub,
    isMonitorSub,
    isCoolingSub,
    isPsuSub,
    isWebcamSub,
  ]);

  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];

    if (sortMode === 'price-asc') {
      list.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else if (sortMode === 'price-desc') {
      list.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    } else if (sortMode === 'name-asc') {
      list.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
    }

    return list;
  }, [filteredProducts, sortMode]);

  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / PRODUCTS_PER_PAGE));
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);

  const paginatedProducts = useMemo(() => {
    const start = (safeCurrentPage - 1) * PRODUCTS_PER_PAGE;
    return sortedProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [safeCurrentPage, sortedProducts]);

  useEffect(() => {
    if (currentPage === safeCurrentPage) return;
    const next = new URLSearchParams(searchParams.toString());
    if (safeCurrentPage <= 1) next.delete('page');
    else next.set('page', String(safeCurrentPage));
    const query = next.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
  }, [currentPage, pathname, router, safeCurrentPage, searchParams]);

  const goToPage = (page: number) => {
    const normalized = Math.min(Math.max(page, 1), totalPages);
    const next = new URLSearchParams(searchParams.toString());
    if (normalized <= 1) next.delete('page');
    else next.set('page', String(normalized));
    const query = next.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
  };

  const pageNumbers = useMemo(() => {
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, safeCurrentPage - half);
    const end = Math.min(totalPages, start + windowSize - 1);
    if (end - start + 1 < windowSize) {
      start = Math.max(1, end - windowSize + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  }, [safeCurrentPage, totalPages]);

  return (
    <div className="space-y-4 [content-visibility:auto]">
      <div className="modern-card p-4 md:p-5">
        <div className="flex flex-wrap items-start justify-end gap-4">
          <div className="flex items-center gap-2 relative">
            <span className="text-sm font-medium text-slate-500">Sắp xếp</span>
            <div className="relative">
              <button
                onClick={() => setIsSortOpen(!isSortOpen)}
                onBlur={() => setTimeout(() => setIsSortOpen(false), 200)}
                className="flex items-center justify-between w-44 bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm text-slate-800 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-[border-color,box-shadow] duration-200"
              >
                <span>
                  {sortMode === 'latest'
                    ? 'Mới nhất'
                    : sortMode === 'price-asc'
                      ? 'Giá thấp → cao'
                      : sortMode === 'price-desc'
                        ? 'Giá cao → thấp'
                        : 'Tên A → Z'}
                </span>
                <svg
                  className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isSortOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isSortOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden py-1">
                  {(
                    [
                      { value: 'latest', label: 'Mới nhất' },
                      { value: 'price-asc', label: 'Giá thấp → cao' },
                      { value: 'price-desc', label: 'Giá cao → thấp' },
                      { value: 'name-asc', label: 'Tên A → Z' },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortMode(option.value);
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        sortMode === option.value
                          ? 'bg-blue-600 text-white font-medium'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {sortedProducts.length ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {paginatedProducts.map((product) => (
              <CategoryProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="modern-card p-3 md:p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600">
                Trang <span className="font-semibold">{safeCurrentPage}</span> / {totalPages}
              </p>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => goToPage(safeCurrentPage - 1)}
                  disabled={safeCurrentPage <= 1}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
                >
                  Trước
                </button>
                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => goToPage(page)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                      page === safeCurrentPage
                        ? 'bg-blue-600 text-white'
                        : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => goToPage(safeCurrentPage + 1)}
                  disabled={safeCurrentPage >= totalPages}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
                >
                  Sau
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="modern-card p-10 text-center text-slate-500">
          Chưa có sản phẩm trong danh mục này.
        </div>
      )}
    </div>
  );
}
